/**
 * WebSocket lifecycle for useChatApp: connect, retire, reconnect on account
 * change, and parsing agent events into thread messages. Split out of
 * useChatApp.js unchanged (2026-09-25 gap closure).
 */
import { useEffect, useRef, useCallback } from 'react';
import { upsertSession, deriveSessionTitle } from '../../utils/chatSessions';
import {
  buildConfirmationCardMessage,
  findConfirmationCard,
  isConfirmableCard,
  markCardResolved,
  sanitizeMessagesForPersist,
  markSocketLost,
  staleSubmittingCards,
  staleCardIfPending,
} from '../../utils/confirmationCards';
import { parseToolEvidence, attachEvidenceToAnswer, MAX_TURN_EVIDENCE } from '../../utils/toolEvidence';
import { attachRecordIdToAnswer, isRecordId } from '../../utils/turnRating';
import { safeStorage } from '../../utils/safeStorage';
import { DRAFT_REPLY_MAX_CHARS } from '../../utils/reportDraft';
import { nextMessageId } from '../../utils/messageIds';
import {
  AGENT_CAP_RETRY_MS,
  AUTH_ERROR_CODES,
  agentDropReconnectDelayMs,
  ERROR_DONE_GRACE_MS,
  TOO_MANY_CONNECTIONS_CLOSE_CODE,
  TOO_MANY_CONNECTIONS_CODE,
  TOO_MANY_CONNECTIONS_MESSAGE,
  TURN_WATCHDOG_MESSAGE,
  TURN_WATCHDOG_MS,
  isValidWsUrl,
  normalizeWsUrlForEnv,
} from './chatConstants';

/**
 * @param {object} s - Context from useChatState.
 * @param {{ userId: string|null, onUnauthorized?: Function }} opts
 * @param {{ speakReply: (text: string) => void }} voice
 */
export function useChatSocket(
  s,
  { userId, onUnauthorized: onUnauthorizedProp, onTermsRequired: onTermsRequiredProp },
  { speakReply },
) {
  // Read through a ref: a new callback identity (e.g. an inline prop) must
  // never rebuild the handler and reconnect the socket (2026-09-25 gap closure).
  const onUnauthorizedRef = useRef(onUnauthorizedProp);
  onUnauthorizedRef.current = onUnauthorizedProp;
  // Consent outstanding (audit P-4): the app re-reads /me and shows the terms.
  const onTermsRequiredRef = useRef(onTermsRequiredProp);
  onTermsRequiredRef.current = onTermsRequiredProp;
  const {
    setMessages, messagesRef, setIsConnected, endTurn, setWs, setConnectionError, setIsRetrying,
    backendUrl, setBackendUrl, expectAwosReplyRef, lastAssistantTextRef,
    submittingConfirmationIdRef, reconnectRef, confirmationExpiryTimersRef, wsRef,
    sessionIdRef, loadedForUserRef, inFlightRef, turnEpochRef, turnWatchdogRef, errorGraceRef,
    turnEvidenceRef, draftRef,
    startTurnWatchdogRef,
  } = s;
  // The one pending reconnect after a cap refusal (2026-09-25 adv pass 14).
  const capRetryRef = useRef(null);
  // The socket the agent refused with error{code:'too_many_connections'}: its
  // close is a cap refusal whatever code a proxy delivers (adv pass 15).
  const capRefusedSocketRef = useRef(null);
  // Unexpected closes since the last `done`: the drop-reconnect backoff step
  // (2026-09-25 adversarial pass 19). The pending timer is capRetryRef, so a
  // manual connect or unmount cancels it too.
  const dropStreakRef = useRef(0);

  useEffect(() => {
    return () => {
      clearTimeout(capRetryRef.current);
      capRetryRef.current = null;
      confirmationExpiryTimersRef.current.forEach((timerId) => clearTimeout(timerId));
      confirmationExpiryTimersRef.current = [];
      // Turn timers too: unmount skips endTurn when no socket is left to retire.
      clearTimeout(turnWatchdogRef.current);
      clearTimeout(errorGraceRef.current);
    };
  }, [confirmationExpiryTimersRef, turnWatchdogRef, errorGraceRef]);

  /**
   * Closes the current socket and applies the agent's rule that a socket's
   * confirmation ledger dies with it: pending cards go stale, a card that was
   * submitting becomes 'unknown'. The retired socket's own onclose is ignored
   * by the identity guard, so every teardown path must go through here.
   */
  const retireCurrentSocket = useCallback((reason) => {
    const current = wsRef.current;
    if (!current) return;
    wsRef.current = null;
    submittingConfirmationIdRef.current = null;
    setMessages((prev) => markSocketLost(prev));
    setWs(null);
    setIsConnected(false);
    // Any turn on the retired socket can no longer finish.
    endTurn();
    current.close(1000, reason);
  }, [endTurn, setIsConnected, setMessages, setWs, submittingConfirmationIdRef, wsRef]);

  /**
   * Ends a turn that will never get `done` (2026-09-25 adversarial pass 1):
   * the old agent sends `error` with no `done`, and any agent can hang. The
   * socket is replaced, not reused, so the old turn's late frames are dropped
   * by the identity guard and cannot close or write into the next turn; a
   * submitting card goes 'unknown' (the server may have run it), never retry.
   * No-op unless the same turn is still in flight on the same socket.
   * @param {WebSocket} socket - The socket the turn was sent on.
   * @param {number} epoch - turnEpochRef when the timer was armed.
   * @param {string|null} notice - Error bubble to add, if any.
   */
  const abandonStuckTurn = useCallback((socket, epoch, notice) => {
    if (wsRef.current !== socket || turnEpochRef.current !== epoch || !inFlightRef.current) return;
    if (notice) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: notice,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    }
    lastAssistantTextRef.current = '';
    expectAwosReplyRef.current = false;
    retireCurrentSocket('Turn did not finish');
    if (reconnectRef.current) reconnectRef.current();
  }, [wsRef, turnEpochRef, inFlightRef, setMessages, lastAssistantTextRef, expectAwosReplyRef, retireCurrentSocket, reconnectRef]);

  /**
   * Arms the no-`done` watchdog for the turn just sent on `socket`. Cleared by
   * endTurn (done, retire, chat switch, sign-out) and on unmount.
   * @param {WebSocket} socket
   */
  const startTurnWatchdog = useCallback((socket) => {
    clearTimeout(turnWatchdogRef.current);
    const epoch = turnEpochRef.current;
    turnWatchdogRef.current = setTimeout(() => {
      turnWatchdogRef.current = null;
      abandonStuckTurn(socket, epoch, TURN_WATCHDOG_MESSAGE);
    }, TURN_WATCHDOG_MS);
  }, [turnWatchdogRef, turnEpochRef, abandonStuckTurn]);
  startTurnWatchdogRef.current = startTurnWatchdog;

  /**
   * Dev-only: change agent WebSocket URL and force reconnect.
   * @param {string} newUrl
   */
  const handleUrlChange = (newUrl) => {
    setBackendUrl(newUrl);
    safeStorage.set('backendUrl', newUrl);
    setConnectionError(null);
    retireCurrentSocket('URL changed');
  };

  /**
   * Parses agent WebSocket payloads into chat messages.
   * @param {{ type: string, text?: string, error?: string }} data
   */
  const handleWebSocketMessage = useCallback((data) => {
    // Any frame after an `error` means the agent is still talking: the new
    // agent's `done` (or a card, then `done`) cancels the grace below.
    clearTimeout(errorGraceRef.current);
    errorGraceRef.current = null;
    // Report-draft turn (plan Track 5 B6, 2026-09-30): the agent's reply is
    // captured for the form, not shown as a chat bubble, and a card it stages
    // is dropped unseen (so it can never be confirmed; nothing is submitted).
    // `done`/`error` fall through to the normal handling below.
    const draft = draftRef.current;
    if (draft && data.type === 'text') {
      const chunk = typeof data.text === 'string' ? data.text : '';
      if (draft.text.length + chunk.length > DRAFT_REPLY_MAX_CHARS) draft.overflow = true;
      else draft.text += chunk;
      return;
    }
    if (draft && data.type === 'confirmation_request') {
      console.warn('Ignoring a confirmation_request staged during a report-draft turn');
      return;
    }
    if (data.type === 'text') {
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'assistant',
          content: data.text,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      // Keep the loading indicator until the agent sends `done`. Clearing it
      // on the first text drops the spinner after filler like "please hold on"
      // while tools are still running.

      const next = typeof data.text === 'string' ? data.text : '';
      const prev = lastAssistantTextRef.current || '';
      const isReport = (value) =>
        /sitrep|risk assessment|risk score|temperature:|executive summary|coordinates:/i.test(
          value,
        );
      if (isReport(next) || !prev || (next.length > prev.length && !isReport(prev))) {
        lastAssistantTextRef.current = next;
      }
    } else if (data.type === 'confirmation_request') {
      const card = buildConfirmationCardMessage(data);
      if (!card) {
        // External bytes are hostile — drop a malformed event rather than
        // rendering a broken/unconfirmable card.
        console.error('Malformed confirmation_request event, ignoring:', data);
        return;
      }
      // A reused id would make confirmReport ambiguous: keep the first card
      // and drop the repeat (review B3 F3, 2026-09-29). No user content logged.
      if (findConfirmationCard(messagesRef.current, card.confirmationId)) {
        console.warn('Duplicate confirmation_request id, ignoring the repeat');
        return;
      }
      setMessages((prev) => (
        findConfirmationCard(prev, card.confirmationId) ? prev : [...prev, card]
      ));
      if (!isConfirmableCard(card)) {
        // Unknown tool/schema_version or a proposal failing its model: shown
        // as "not submitted", never confirmable, so no expiry timer
        // (plan 05 B3, 2026-09-29).
        console.warn('Unsupported confirmation_request (not displayable):', card.tool, card.schemaVersion);
        return;
      }
      const ttlMs = Math.max(0, card.expiresAt - Date.now());
      const timerId = setTimeout(() => {
        setMessages((prev) => staleCardIfPending(prev, card.confirmationId));
      }, ttlMs);
      confirmationExpiryTimersRef.current.push(timerId);
    } else if (data.type === 'tool_result') {
      // The tool's envelope/v1 (source, observation time, coverage). Collect
      // it for the turn's citation; attached to the answer at `done`. Bounded
      // so a looping agent cannot grow the turn without limit (plan 05 C7).
      // Only while a turn is in flight: a frame from a turn that already ended
      // (e.g. after a chat switch) is ignored, so evidence never crosses turns
      // or chats (review C7 M1).
      const evidence = inFlightRef.current ? parseToolEvidence(data.content) : null;
      if (evidence && turnEvidenceRef.current.length < MAX_TURN_EVIDENCE) {
        turnEvidenceRef.current.push(evidence);
      }
    } else if (data.type === 'done') {
      dropStreakRef.current = 0;
      if (draft) {
        draftRef.current = null;
        draft.resolve(
          draft.error
            ? { ok: false, reason: 'agent-error' }
            : { ok: !draft.overflow, reason: draft.overflow ? 'invalid' : undefined, text: draft.text },
        );
      }
      if (turnEvidenceRef.current.length > 0) {
        const collected = turnEvidenceRef.current;
        setMessages((prev) => attachEvidenceToAnswer(prev, collected));
      }
      // The turn's id, so the answer can be rated (docs/guardian/03 §1.4).
      // Not for a report-draft turn: that text never becomes a chat answer.
      if (!draft && isRecordId(data.record_id)) {
        const recordId = data.record_id;
        setMessages((prev) => attachRecordIdToAnswer(prev, recordId));
      }
      endTurn(); // clears turnEvidenceRef
      if (submittingConfirmationIdRef.current) {
        const resolvedId = submittingConfirmationIdRef.current;
        submittingConfirmationIdRef.current = null;
        setMessages((prev) => markCardResolved(prev, resolvedId));
      }
      const speakText = lastAssistantTextRef.current;
      lastAssistantTextRef.current = '';
      if (expectAwosReplyRef.current && speakText) {
        expectAwosReplyRef.current = false;
        speakReply(speakText);
      }
    } else if (data.type === 'error') {
      if (draft) draft.error = true;
      // Machine-readable agent auth codes (2026-09-24): an expired/invalid
      // Guardian token signs out and reopens sign-in (the userId change then
      // reconnects); a socket bound to another account is replaced.
      if (AUTH_ERROR_CODES.has(data.code) && submittingConfirmationIdRef.current) {
        submittingConfirmationIdRef.current = null;
        setMessages((prev) => staleSubmittingCards(prev));
        // The server refused before running anything: persist 'stale' now,
        // for the owning user, before a sign-out can skip the persist effect
        // and leave the saved copy at 'unknown' (pass 18).
        const owner = loadedForUserRef.current;
        const ownedSessionId = sessionIdRef.current;
        if (owner && ownedSessionId) {
          const staled = staleSubmittingCards(messagesRef.current);
          upsertSession(owner, {
            id: ownedSessionId,
            title: deriveSessionTitle(staled),
            messages: sanitizeMessagesForPersist(staled),
          });
        }
      }
      if (data.code === 'terms_required' && onTermsRequiredRef.current) {
        onTermsRequiredRef.current();
      }
      if (data.code === 'auth_required' && onUnauthorizedRef.current) {
        onUnauthorizedRef.current('Your session expired. Please sign in again.');
      } else if (data.code === 'account_mismatch' && reconnectRef.current) {
        reconnectRef.current();
      } else if (data.code === TOO_MANY_CONNECTIONS_CODE) {
        capRefusedSocketRef.current = wsRef.current;
      }
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'error',
          content: `Error: ${data.error}`,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      // The turn ends at `done`, which the new agent sends right after every
      // error; ending it here let a new send start before the old `done`
      // arrived.
      // 2026-09-25 adversarial pass 1: the old agent never sends that `done`
      // (rollout runs this client against it), so an error followed by
      // nothing for ERROR_DONE_GRACE_MS ends the turn and replaces the socket;
      // a `done` later than that lands on the retired socket and is dropped.
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        endTurn();
      } else if (inFlightRef.current) {
        const socket = wsRef.current;
        const epoch = turnEpochRef.current;
        errorGraceRef.current = setTimeout(() => {
          errorGraceRef.current = null;
          abandonStuckTurn(socket, epoch, null);
        }, ERROR_DONE_GRACE_MS);
      }
    }
  }, [setMessages, draftRef, lastAssistantTextRef, confirmationExpiryTimersRef, endTurn, submittingConfirmationIdRef, expectAwosReplyRef, speakReply, reconnectRef, wsRef, loadedForUserRef, sessionIdRef, messagesRef, onUnauthorizedRef, errorGraceRef, inFlightRef, turnEpochRef, turnEvidenceRef, abandonStuckTurn]);

  /** Opens (or reopens) the WebSocket to the langchain-agent server. */
  const connectWebSocket = useCallback(() => {
    const urlToUse = normalizeWsUrlForEnv(backendUrl);
    if (!isValidWsUrl(urlToUse)) {
      setConnectionError('Invalid WebSocket URL. It must start with ws:// or wss://');
      return;
    }

    // A fresh connection re-verifies the account and has its own ledger.
    clearTimeout(capRetryRef.current);
    capRetryRef.current = null;
    retireCurrentSocket('Reconnecting');

    try {
      setConnectionError(null);
      setIsRetrying(true);

      const websocket = new WebSocket(urlToUse);
      wsRef.current = websocket;

      websocket.onopen = () => {
        if (wsRef.current !== websocket) return;
        setIsConnected(true);
        setWs(websocket);
        setConnectionError(null);
        setIsRetrying(false);
      };

      websocket.onmessage = (event) => {
        if (wsRef.current !== websocket) return;
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      websocket.onclose = (event) => {
        if (wsRef.current !== websocket) return;
        wsRef.current = null;
        setIsConnected(false);
        setWs(null);
        setIsRetrying(false);
        // A dropped socket takes its confirmation ledger and turn with it.
        submittingConfirmationIdRef.current = null;
        setMessages((prev) => markSocketLost(prev));
        endTurn();
        if (event.code === 4401 && onUnauthorizedRef.current) {
          onUnauthorizedRef.current('Your session expired. Please sign in again.');
        }

        const capRefused = capRefusedSocketRef.current === websocket;
        capRefusedSocketRef.current = null;
        if (event.code === TOO_MANY_CONNECTIONS_CLOSE_CODE || capRefused) {
          // Refused at the agent's per-account cap: say why, and reconnect
          // once so the composer works again when a slot frees (adv pass 14).
          setConnectionError(TOO_MANY_CONNECTIONS_MESSAGE);
          clearTimeout(capRetryRef.current);
          capRetryRef.current = setTimeout(() => {
            capRetryRef.current = null;
            if (!wsRef.current && reconnectRef.current) reconnectRef.current();
          }, AGENT_CAP_RETRY_MS);
        } else {
          if (event.code !== 1000) {
            setConnectionError(`Connection closed unexpectedly (Code: ${event.code})`);
          }
          // 2026-09-25 adversarial pass 19: any other close (1011 after an
          // agent internal error, a network drop) reconnects after a backoff;
          // 4401 is left to sign-out, whose userId change reconnects.
          if (event.code !== 4401) {
            const delay = agentDropReconnectDelayMs(dropStreakRef.current);
            dropStreakRef.current += 1;
            clearTimeout(capRetryRef.current);
            capRetryRef.current = setTimeout(() => {
              capRetryRef.current = null;
              if (!wsRef.current && reconnectRef.current) reconnectRef.current();
            }, delay);
          }
        }
      };

      websocket.onerror = () => {
        if (wsRef.current !== websocket) return;
        setIsRetrying(false);
        setConnectionError('Failed to connect to WebSocket server. Please check the URL and try again.');
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      wsRef.current = null;
      setConnectionError('Invalid WebSocket URL. Please check the format and try again.');
      setIsRetrying(false);
    }
  }, [backendUrl, retireCurrentSocket, setConnectionError, setIsRetrying, wsRef, setIsConnected, setWs, handleWebSocketMessage, submittingConfirmationIdRef, setMessages, endTurn, onUnauthorizedRef, reconnectRef]);
  reconnectRef.current = connectWebSocket;

  // Connect on mount / URL change; close cleanly on unmount.
  useEffect(() => {
    connectWebSocket();
    return () => {
      retireCurrentSocket('Component unmounting');
    };
  }, [connectWebSocket, retireCurrentSocket]);

  // The agent binds each socket to the first verified account that uses it
  // and refuses a different account on the same socket. Reconnect whenever
  // the signed-in Guardian user changes (sign-in, sign-out, account switch).
  const connectedUserRef = useRef(userId);
  useEffect(() => {
    if (connectedUserRef.current === userId) return;
    connectedUserRef.current = userId;
    connectWebSocket();
  }, [userId, connectWebSocket]);

  /** Clears the last connection error and attempts a fresh WebSocket connect. */
  const retryConnection = () => {
    setConnectionError(null);
    connectWebSocket();
  };

  return {
    retireCurrentSocket,
    handleUrlChange,
    connectWebSocket,
    retryConnection,
  };
}
